<script setup>
import { computed, ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';

const emit = defineEmits(['close', 'created']);

const { t } = useI18n();
const store = useStore();
const dialogRef = ref(null);

const searchQuery = ref('');
const isCreating = ref(false);

const agents = useMapGetter('agents/getAgents');
const currentUser = useMapGetter('getCurrentUser');

const filteredAgents = computed(() => {
  const query = searchQuery.value.toLowerCase().trim();
  return agents.value
    .filter(agent => agent.id !== currentUser.value?.id)
    .filter(agent => {
      if (!query) return true;
      return (
        agent.name?.toLowerCase().includes(query) ||
        agent.email?.toLowerCase().includes(query)
      );
    });
});

const startChat = async agent => {
  if (isCreating.value) return;

  isCreating.value = true;
  try {
    const chat = await store.dispatch('staffChats/createStaffChat', {
      userId: agent.id,
    });
    dialogRef.value?.close();
    emit('created', chat);
  } catch {
    useAlert(t('STAFF_CHAT.CREATE_ERROR'));
  } finally {
    isCreating.value = false;
  }
};

const handleClose = () => {
  emit('close');
};

onMounted(() => {
  dialogRef.value?.open();
});
</script>

<template>
  <Dialog
    ref="dialogRef"
    type="edit"
    :title="$t('STAFF_CHAT.SELECT_TEAM_MEMBER')"
    :show-cancel-button="false"
    :show-confirm-button="false"
    @close="handleClose"
  >
    <div class="flex flex-col gap-3 min-w-[20rem]">
      <input
        v-model="searchQuery"
        type="search"
        class="w-full px-3 py-2 text-sm border rounded-lg border-n-weak bg-n-solid-2 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand"
        :placeholder="$t('STAFF_CHAT.SEARCH_PLACEHOLDER')"
      />
      <ul class="flex flex-col gap-1 max-h-64 overflow-y-auto">
        <li v-for="agent in filteredAgents" :key="agent.id">
          <button
            type="button"
            class="flex items-center w-full gap-3 px-3 py-2 rounded-lg hover:bg-n-alpha-2"
            :disabled="isCreating"
            @click="startChat(agent)"
          >
            <Avatar
              :name="agent.name"
              :src="agent.thumbnail"
              :size="32"
              rounded-full
            />
            <div class="flex flex-col items-start min-w-0">
              <span class="text-sm font-medium truncate text-n-slate-12">
                {{ agent.name }}
              </span>
              <span class="text-xs truncate text-n-slate-11">
                {{ agent.email }}
              </span>
            </div>
          </button>
        </li>
      </ul>
    </div>
    <template #footer />
  </Dialog>
</template>
